import { Col, Row, Spinner } from "react-bootstrap";
import { MonthView, Scheduler, SchedulerSlotProps, WeekView } from "@progress/kendo-react-scheduler";
import "@progress/kendo-date-math/tz/all";
import { useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { AppointmentContextMenu, CalendarBookingModal, CalendarViewItem, HeldAppointmentChip, MonthViewSlot, NameTitleTimeItem, SchedulerContextHeader, TitleStartItem, BlockedTimeSlot, BlockedTimeEditSlot } from "./components";
import { ParkedAppointment } from "./hooks/useAppointmentClipboard";
import { SearchableLocationDropdown, SearchableUserDropdown, SpinnerDefault, TitlelessCard } from "../../components";
import { usePermission, useSetPageTitle } from "../../hooks";
import { useAppointmentClipboard, useCalendarEvents, useCalendarUserSelection, useStickySchedulerChrome } from "./hooks";
import { isLocationAdmin } from "../../app";
import { PermissionDefinitions } from "../../security";
import { useCalendarUserSchedules } from "./hooks/useCalendarUserSchedule";
import { AttendanceStatus } from "../../data/entities";

export function CalendarIndexPage() {

  const calendarPermission = useMemo(() => PermissionDefinitions.calendar.getAll(), []);
  const canGetAllProviders = usePermission(calendarPermission);
  const locationAdmin = useSelector(isLocationAdmin());

  useSetPageTitle(`${(canGetAllProviders || locationAdmin) ? "Provider" : "My"} Calendar`, []);

  const { 
    date, 
    mapResult, 
    isLoading, 
    setCalendarGrouping, 
    handleInputChange, 
    handleManualDataChange,
    refreshCalendarEvents,
    checkDateRangeAndRefresh,
    updateAttendanceStatus,
    findCalendarEvent,
    moveCalendarEvent
  } = useCalendarEvents();

  const {
    selectedUserId,
    selectedLocationId,
    userOptions,
    locationOptions,
    isInitialized,
    currentUserDetails,
    showUserDefaultItem,
    handleSelectUser,
    handleSelectLocation,
  } = useCalendarUserSelection();

  const { schedules: scheduleFromHook, isLoading: schedulesLoading } = useCalendarUserSchedules(
    selectedUserId ? parseInt(selectedUserId) : null
  );
  
  const schedules = useMemo(() => 
    selectedLocationId === "" ? [] : scheduleFromHook, 
    [selectedLocationId, scheduleFromHook]
  );

  const stableFilteredDetails = useMemo(() => {
    if (!selectedLocationId || selectedLocationId === "") {
      return currentUserDetails;
    }
    return currentUserDetails.filter(detail => detail.locationId.toString() === selectedLocationId);
  }, [currentUserDetails, selectedLocationId]);
  
  const schedulerContainerRef = useStickySchedulerChrome(isInitialized);

  const prevDateRef = useRef(date);
  const prevUserIdRef = useRef(selectedUserId);
  const prevLocationIdRef = useRef(selectedLocationId);

  const dataChangeRef = useRef(handleManualDataChange);
  dataChangeRef.current = handleManualDataChange;

  const handlePasteAppointment = useCallback((item: any) => {
    dataChangeRef.current({ created: [item], updated: [], deleted: [] } as any);
  }, []);

  const handleMoveAppointment = useCallback((
    parkedItem: ParkedAppointment,
    target: { start: Date; end: Date; providerId?: number }
  ) => {
    if (!parkedItem.sourceEvent) return;
    moveCalendarEvent(parkedItem.sourceEvent, target);
  }, [moveCalendarEvent]);

  const {
    parked,
    menu,
    menuRef,
    openItemMenu,
    openSlotMenu,
    copyFromMenu,
    cutFromMenu,
    placeFromMenu,
    clearParked,
    closeMenu,
  } = useAppointmentClipboard({
    onPasteAppointment: handlePasteAppointment,
    onMoveAppointment: handleMoveAppointment,
    findCalendarEvent,
  });


  const visibleEvents = useMemo(() =>
    parked?.mode === "cut" && parked.bookingEventId != null
      ? mapResult.filter(e => e.bookingEventId !== parked.bookingEventId)
      : mapResult,
  [mapResult, parked]);

  const handleMenuSetAttendance = useCallback((dataItem: any, status: AttendanceStatus) => {
    updateAttendanceStatus(dataItem.bookingEventId, status);
    closeMenu();
  }, [updateAttendanceStatus, closeMenu]);

  useEffect(() => {
    if (!isInitialized || !selectedUserId) return;

    const userOrLocationChanged = 
      prevUserIdRef.current !== selectedUserId || 
      prevLocationIdRef.current !== selectedLocationId;

    prevUserIdRef.current = selectedUserId;
    prevLocationIdRef.current = selectedLocationId;

    const detailsForGrouping = selectedLocationId === "" 
      ? currentUserDetails 
      : currentUserDetails.filter(detail => detail.locationId.toString() === selectedLocationId);

    // Update calendar grouping based on location selection
    if (selectedLocationId === "") {
      setCalendarGrouping("location", detailsForGrouping, "locationId", "locationName", true);
    } else {
      setCalendarGrouping("provider", detailsForGrouping, "providerId", "providerName");
    }

    // Only fetch if user/location actually changed
    if (userOrLocationChanged) {
      refreshCalendarEvents("getByUserLocationAsync", selectedUserId, selectedLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, selectedLocationId, isInitialized]);

  // Check if date navigation requires fetching new data (when navigating outside cached range)
  useEffect(() => {
    if (!isInitialized || !selectedUserId) return;
    
    if (prevDateRef.current.getTime() !== date.getTime()) {
      prevDateRef.current = date;
      checkDateRangeAndRefresh("getByUserLocationAsync", selectedUserId, selectedLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, isInitialized, selectedUserId, selectedLocationId]);

  const memoizedFormComponent = useCallback((formProps: any) => (
    <CalendarBookingModal
      {...formProps}
      dropdownResults={stableFilteredDetails}
      onDeleteAppointment={(dataItem: any) =>
        dataChangeRef.current({ created: [], updated: [], deleted: [dataItem] } as any)
      }
    />
  ), [stableFilteredDetails]);

  const memoizedSlot = useCallback((props: SchedulerSlotProps) => (
    <BlockedTimeSlot
      {...props}
      schedules={schedules}
      providers={stableFilteredDetails}
      isAllLocationsView={selectedLocationId === ""}
      onSlotContextMenu={openSlotMenu}
    />
  ), [schedules, stableFilteredDetails, selectedLocationId, openSlotMenu]);

  const memoizedItem = useCallback((itemProps: any) => (
    <NameTitleTimeItem
      {...itemProps}
      onItemContextMenu={openItemMenu}
    />
  ), [openItemMenu]);

  const memoizedEditSlot = useCallback((props: any) => (
    <BlockedTimeEditSlot
      {...props}
      schedules={schedules}
      providers={stableFilteredDetails}
      isAllLocationsView={selectedLocationId === ""}
    />
  ), [schedules, stableFilteredDetails, selectedLocationId]);

  const memoizedViewSlot = useCallback((props: any) => (
    <MonthViewSlot 
      {...props} 
      customStyle={{ minHeight: 120 }}
      schedules={schedules}
      providers={stableFilteredDetails}
      isAllLocationsView={selectedLocationId === ""}
    />
  ), [schedules, stableFilteredDetails, selectedLocationId]);

  const memoizedViewItem = useCallback((props: any) => (
    <CalendarViewItem
      {...props}
      customStyling={{ height: "auto" }}
    />
  ), []);

  const schedulerUserName = useMemo(() =>
    userOptions.find(o => o.value === selectedUserId)?.text,
  [userOptions, selectedUserId]);

  const schedulerLocationName = useMemo(() =>
    selectedLocationId === ""
      ? "All Locations"
      : locationOptions.find(o => o.value === selectedLocationId)?.text,
  [locationOptions, selectedLocationId]);

  const memoizedHeader = useCallback((headerProps: any) => (
    <SchedulerContextHeader
      {...headerProps}
      name={schedulerUserName}
      location={schedulerLocationName}
      extraChip={parked && <HeldAppointmentChip parked={parked} onClear={clearParked} />}
    />
  ), [schedulerUserName, schedulerLocationName, parked, clearParked]);
  
  if (!isInitialized) {
    return <SpinnerDefault />;
  }

  return (
    <TitlelessCard bodyClassName="p-0">
      <Row>
        <Col xs={24} sm={12} md={12}>
          <div>
            <SearchableUserDropdown
              defaultItem={showUserDefaultItem}
              name="user"
              users={userOptions}
              value={selectedUserId}
              onChange={handleSelectUser}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <div>
            <SearchableLocationDropdown
              name="location"
              locations={locationOptions}
              value={selectedLocationId}
              onChange={handleSelectLocation}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="scheduler-container scheduler-sticky-header" ref={schedulerContainerRef}>
            {(isLoading || schedulesLoading) && (
              <div className="scheduler-overlay">
                <Spinner 
                  size="sm" 
                  animation="border" 
                  variant="primary" 
                  style={{ width: "5rem", height: "5rem", borderWidth: "0.5rem" }}
                />
              </div>
            )}
            <Scheduler
              editable
              timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
              form={memoizedFormComponent}
              header={memoizedHeader}
              data={visibleEvents}
              date={date}
              onDateChange={({ value }) => handleInputChange(value, "dateSelection")}
              onDataChange={handleManualDataChange}
              height="100%"
              viewItem={memoizedViewItem}
              //editSlot={memoizedEditSlot} 
            >
              <WeekView
                title="Full Week"
                workDayStart="07:00"
                workDayEnd="22:00"
                slotDuration={60}
                slotDivisions={4}
                slot={memoizedSlot}
                item={memoizedItem}
              />
              <MonthView
                title="Month"
                itemsPerSlot={20}
                viewSlot={memoizedViewSlot}
                item={TitleStartItem}
              />
            </Scheduler>
            <AppointmentContextMenu
              menu={menu}
              menuRef={menuRef}
              parked={parked}
              onCopy={copyFromMenu}
              onCut={cutFromMenu}
              onPlace={placeFromMenu}
              onSetAttendance={handleMenuSetAttendance}
            />
          </div>
        </Col>
      </Row>
    </TitlelessCard>
  );
}

export default CalendarIndexPage;